import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Certification, CertificationCreateRequest, CertificationUpdateRequest } from '../models/certification.model';

interface CertificationResponse {
  success: boolean;
  data?: {
    certification?: Certification;
    certifications?: Certification[];
  };
  certification?: Certification;
  certifications?: Certification[];
  error?: string;
}

/**
 * Certification Service
 *
 * Handles API calls for certification management in the admin portal.
 * Supports both organization and member certifications.
 */
@Injectable({
  providedIn: 'root'
})
export class CertificationService {
  private readonly baseUrl = `${environment.apiUrl}/admin/certifications`;

  constructor(private http: HttpClient) {}

  /**
   * Get organization certifications
   * GET /admin/certifications?organizationId=X&ownerType=organization
   */
  getOrganizationCertifications(organizationId: string): Observable<Certification[]> {
    const params = new HttpParams()
      .set('organizationId', organizationId)
      .set('ownerType', 'organization');

    return new Observable(observer => {
      this.http.get<CertificationResponse>(this.baseUrl, { params }).subscribe({
        next: (response) => {
          const certifications = response.certifications || response.data?.certifications || [];
          observer.next(certifications);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Get member certifications
   * GET /admin/certifications?profileId=X&ownerType=profile
   */
  getMemberCertifications(profileId: string): Observable<Certification[]> {
    const params = new HttpParams()
      .set('profileId', profileId)
      .set('ownerType', 'profile');

    return new Observable(observer => {
      this.http.get<CertificationResponse>(this.baseUrl, { params }).subscribe({
        next: (response) => {
          const certifications = response.certifications || response.data?.certifications || [];
          observer.next(certifications);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Get certification by ID
   * GET /admin/certifications/:id
   */
  getCertification(id: string): Observable<Certification> {
    return new Observable(observer => {
      this.http.get<CertificationResponse>(`${this.baseUrl}/${id}`).subscribe({
        next: (response) => {
          const certification = response.certification || response.data?.certification;
          if (certification) {
            observer.next(certification);
            observer.complete();
          } else {
            observer.error(new Error('Certification not found'));
          }
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Upload organization certification
   * POST /admin/certifications
   */
  uploadOrganizationCertification(organizationId: string, data: CertificationCreateRequest): Observable<Certification> {
    const payload = {
      ...data,
      ownerType: 'organization',
      organizationId
    };

    return new Observable(observer => {
      this.http.post<CertificationResponse>(this.baseUrl, payload).subscribe({
        next: (response) => {
          const certification = response.certification || response.data?.certification;
          if (certification) {
            observer.next(certification);
            observer.complete();
          } else {
            observer.error(new Error('Failed to create certification'));
          }
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Upload member certification
   * POST /admin/certifications
   */
  uploadMemberCertification(profileId: string, data: CertificationCreateRequest): Observable<Certification> {
    const payload = {
      ...data,
      ownerType: 'profile',
      profileId
    };

    return new Observable(observer => {
      this.http.post<CertificationResponse>(this.baseUrl, payload).subscribe({
        next: (response) => {
          const certification = response.certification || response.data?.certification;
          if (certification) {
            observer.next(certification);
            observer.complete();
          } else {
            observer.error(new Error('Failed to create certification'));
          }
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Update certification
   * PUT /admin/certifications/:id
   */
  updateCertification(id: string, data: CertificationUpdateRequest): Observable<Certification> {
    return new Observable(observer => {
      this.http.put<CertificationResponse>(`${this.baseUrl}/${id}`, data).subscribe({
        next: (response) => {
          const certification = response.certification || response.data?.certification;
          if (certification) {
            observer.next(certification);
            observer.complete();
          } else {
            observer.error(new Error('Failed to update certification'));
          }
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Get pre-signed download URL for certification document
   * GET /admin/certifications/:id/download-url
   */
  getDownloadUrl(id: string): Observable<string> {
    return new Observable(observer => {
      this.http.get<{ downloadUrl: string }>(`${this.baseUrl}/${id}/download-url`).subscribe({
        next: (response) => {
          observer.next(response.downloadUrl);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Delete certification
   * DELETE /admin/certifications/:id
   */
  deleteCertification(id: string): Observable<void> {
    return new Observable(observer => {
      this.http.delete<{ success: boolean }>(`${this.baseUrl}/${id}`).subscribe({
        next: (response) => {
          if (response.success) {
            observer.next();
            observer.complete();
          } else {
            observer.error(new Error('Failed to delete certification'));
          }
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }
}
